/**
 * topicDetailPanelHelper — shared builder for the topic-detail panel content.
 *
 * Two surfaces open the same topic detail: the `/discovery` Progress tab
 * (fresh `right-panel:open`, DiscoverySection) and the understanding-map
 * right panel's row drill (`right-panel:drill`, progressPanelDrillHelper).
 * The content — TopicDetailView markup, disclosure toggles, and the
 * topic-summary hydration of the understanding/threads zones — lives here
 * once so the two cannot drift; each caller supplies its own thread-click
 * behaviour and staleness guard.
 *
 * @module components/topicDetailPanelHelper
 */

import { API } from '../core/api.js';
import { bindSectionChecklist } from './sectionChecklist.js';
import { wireDisclosures, expandDisclosure } from './disclosure.js';
import {
  renderTopicDetailView,
  renderUnderstandingCold,
  renderUnderstandingWarm,
  renderTopicThreads
} from './TopicDetailView.js';

/**
 * Build the topic-detail content element with its disclosure/thread wiring,
 * plus the hydration routine that fills the understanding + threads zones.
 *
 * @param {Object} topic - Topic as `loadTopicProgress` shapes it
 * @param {Object} [opts]
 * @param {((thread: Object) => void)|null} [opts.onThreadClick=null] - Open a
 *   hydrated thread card's detail. When null (a context with no thread-detail
 *   surface), the cards render informational: their button semantics are
 *   stripped after hydration.
 * @returns {{content: HTMLElement, hydrate: (opts?: {stillCurrent?: () => boolean}) => Promise<void>}}
 */
export function buildTopicDetailContent(topic, { onThreadClick = null } = {}) {
  const content = document.createElement('div');
  content.className = 'discovery-detail-content';
  content.innerHTML = renderTopicDetailView(topic);

  // Threads stash from hydration — the card click resolves through it so the
  // handler hands the caller a full thread object, not just an id.
  let threadMap = new Map();

  // The section checklist's per-row chevrons. Delegated on `content`, which is
  // this element's whole lifetime, so it covers BOTH surfaces that render the
  // topic detail through this helper (the Progress-tab drill and the
  // understanding-map drill) — the path drawer's own binding is local to
  // `_loadPathSectionsList` and does not reach here.
  bindSectionChecklist(content);

  // Disclosure headers (Threads / Sections groups) toggle their body via the
  // shared primitive — delegated on `content`, so it survives the async
  // hydration below.
  wireDisclosures(content);

  // Delegated so the handler survives the async hydration below: a thread
  // card opens via the caller's handler.
  const openThread = (card) => {
    const thread = threadMap.get(String(card.dataset.threadId));
    if (thread && onThreadClick) onThreadClick(thread);
  };
  content.addEventListener('click', (e) => {
    const jump = e.target.closest('[data-scroll-to-sections]');
    if (jump) {
      // Expand first if the user had collapsed it — scrolling to a closed box
      // is a dead end, and the count is a promise that the list is there.
      // Keyboard users have to land where the scroll went, not stay behind it.
      expandDisclosure(content, 'topic-sections', { scrollIntoView: true, focus: true });
      return;
    }
    const card = e.target.closest('[data-thread-id]');
    if (card) openThread(card);
  });
  content.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const card = e.target.closest('[data-thread-id]');
    if (card) {
      e.preventDefault();
      openThread(card);
    }
  });

  /**
   * Fetch the topic summary and fill the understanding + threads zones.
   * Fire-and-forget from the caller: the header/overview/sections above are
   * already painted. `stillCurrent` lets a caller with panel-selection state
   * drop a stale response (a detached content element is otherwise harmless).
   * @param {Object} [opts]
   * @param {() => boolean} [opts.stillCurrent]
   * @returns {Promise<void>}
   */
  const hydrate = async ({ stillCurrent = () => true } = {}) => {
    let data = null;
    try {
      data = await API.getTopicSummary(topic.id);
    } catch (err) {
      console.warn('[topicDetailPanelHelper] topic summary fetch failed:', err);
    }
    if (!stillCurrent()) return;

    const understandingZone = content.querySelector('[data-understanding]');
    const ledeZone = content.querySelector('[data-understanding-lede]');
    const threadsZone = content.querySelector('[data-threads]');
    if (!understandingZone) return;

    // Only the lede and body slots swap. The card's head — and the chart
    // mounted into it above — is static markup that must survive hydration.
    const isWarm = !!(data && data.hasData && data.summary);
    const { lede, body } = isWarm
      ? renderUnderstandingWarm(data)
      : renderUnderstandingCold(data?.themes || []);
    if (ledeZone) ledeZone.innerHTML = lede;
    understandingZone.innerHTML = body;

    if (isWarm) {
      const threads = (data.openThreads || []).slice(0, 3);
      threadMap = new Map(threads.map(t => [String(t.id), t]));
      if (threadsZone && threads.length) {
        threadsZone.innerHTML = renderTopicThreads(threads);
        threadsZone.hidden = false;
        if (!onThreadClick) {
          // No thread-detail surface here — the cards are informational.
          threadsZone.querySelectorAll('[data-thread-id]').forEach(card => {
            card.removeAttribute('role');
            card.removeAttribute('tabindex');
          });
        }
      } else if (threadsZone) {
        threadsZone.hidden = true;
      }
    } else if (threadsZone) {
      threadsZone.hidden = true;
    }
  };

  return { content, hydrate };
}
